import React, { useState } from 'react'
import { Calendar, Download, Share2, FileText, TrendingUp, DollarSign, Shield, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector'
import { ExecutiveSummary } from '@/components/analytics/ExecutiveSummary'
import { FraudTrendsChart } from '@/components/analytics/FraudTrendsChart'
import { PatternDistribution } from '@/components/analytics/PatternDistribution'
import { GeographicAnalysis } from '@/components/analytics/GeographicAnalysis'
import { SourcePerformance } from '@/components/analytics/SourcePerformance'
import { TimeAnalysis } from '@/components/analytics/TimeAnalysis'
import { MLPerformance } from '@/components/analytics/MLPerformance'
import { FinancialImpact } from '@/components/analytics/FinancialImpact'
import { ComparativeAnalysis } from '@/components/analytics/ComparativeAnalysis'

export function Analytics() {
  const [timeRange, setTimeRange] = useState<{
    from: Date | null
    to: Date | null
    preset?: string
  }>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
    to: new Date(),
    preset: 'last30days'
  })

  const [compareEnabled, setCompareEnabled] = useState(false)

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    console.log('Exporting PDF...')
  }

  const handleExportCSV = () => {
    // TODO: Implement CSV export
    console.log('Exporting CSV...')
  }

  const handleShareDashboard = () => {
    // TODO: Implement dashboard sharing
    navigator.clipboard.writeText(window.location.href)
    console.log('Dashboard link copied to clipboard')
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground">
            Comprehensive fraud detection insights and performance metrics
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export Data
          </Button>
          <Button variant="outline" size="sm" onClick={handleShareDashboard}>
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      {/* Time Range Selector */}
      <TimeRangeSelector
        value={timeRange}
        onChange={setTimeRange}
        compareEnabled={compareEnabled}
        onCompareToggle={setCompareEnabled}
      />

      {/* Executive Summary */}
      <ExecutiveSummary 
        timeRange={timeRange}
        compareEnabled={compareEnabled}
      />

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="patterns">Patterns</TabsTrigger>
          <TabsTrigger value="sources">Sources</TabsTrigger>
          <TabsTrigger value="geography">Geography</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="financial">Financial</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Main Trends Chart */}
          <FraudTrendsChart 
            timeRange={timeRange}
            compareEnabled={compareEnabled}
          />
          
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Pattern Distribution */}
            <PatternDistribution />
            
            {/* Time Analysis */}
            <TimeAnalysis />
          </div>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-6 mt-6">
          <PatternDistribution detailed={true} />
        </TabsContent>

        <TabsContent value="sources" className="space-y-6 mt-6">
          <SourcePerformance timeRange={timeRange} />
        </TabsContent>

        <TabsContent value="geography" className="space-y-6 mt-6">
          <GeographicAnalysis timeRange={timeRange} />
        </TabsContent>

        <TabsContent value="performance" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <MLPerformance />
            <ComparativeAnalysis 
              timeRange={timeRange}
              compareEnabled={compareEnabled}
            />
          </div>
        </TabsContent>

        <TabsContent value="financial" className="space-y-6 mt-6">
          <FinancialImpact timeRange={timeRange} />
        </TabsContent>
      </Tabs>

      {/* Insights & Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Key Insights & Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-green-100 dark:bg-green-900/40 rounded">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-1">
                    Fraud Rate Declining
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Overall fraud rate has decreased by 5% this month due to improved detection rules.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-yellow-100 dark:bg-yellow-900/40 rounded">
                  <Shield className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
                    Focus on Zip 80112
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    32% of fraudulent leads come from zip 80112. Consider additional screening for this area.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-blue-100 dark:bg-blue-900/40 rounded">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Peak Hours Analysis
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Fraud attempts peak between 9-11 AM and 2-4 PM. Increase monitoring during these hours.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3">
                <div className="p-1 bg-purple-100 dark:bg-purple-900/40 rounded">
                  <DollarSign className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-1">
                    ROI Exceeds 29,000%
                  </h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    System saves $31,170/month vs $105 cost. Consider expanding fraud detection capabilities.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}